import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
import store from "./store";
import router, { isStaleAssetError } from "./router";
import plugins from "./plugins";
import App from "./App.vue";
import "grapesjs/dist/css/grapes.min.css";
import "@sass/index.scss";

// initialize app
const app = createApp(App);

function isProduction() {
  const productionDomains = ["app.apextrading.com", "secure.bushel44.com"];
  return productionDomains.includes(window.location.hostname);
}

const environment = APP_ENVIRONMENT ?? "unset";

// Requests the interceptor in services/api.js owns, or that carry nothing the browser can add:
// no response (offline, timeout, aborted), an ended session or a tab out of sync with the
// server (both reload), a refused permission, and server failures, which the backend and the
// load balancer report themselves.
function isUnreportableRequestError(error) {
  const status = error.response?.status;
  return !status || [401, 403, 444].includes(status) || status >= 500;
}

// A request error's stack is axios internals, so by default every endpoint answering one status
// lands in one issue. Group by what was asked for instead, with ids stripped so one endpoint is
// one issue.
function requestFingerprint(error) {
  const method = (error.config?.method ?? "get").toUpperCase();
  const path = (error.config?.url ?? "")
    .split("?")[0]
    .split("/")
    .map((segment) => (/^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(segment) ? ":id" : segment))
    .join("/");
  return ["axios", method, path, String(error.response.status)];
}

// sentry
Sentry.init({
  app,
  enabled: environment !== "local",
  release: __APP_RELEASE__ || undefined,
  // Messages are reported without a synthetic stack, so they group by their text, carry
  // event.message rather than event.exception, and leave the session healthy.
  attachStacktrace: false,
  // Stale-asset errors (chunk/CSS requests broken by a deploy) are filtered as RAW exceptions
  // only — recovery auto-reloads in router.js, and the unrecovered cases still report
  // deliberately via captureMessage ("Router error", "Vite preload error"), which passes this
  // filter because attachStacktrace is off.
  beforeSend(event, hint) {
    const error = hint?.originalException;
    if (error?.isAxiosError) {
      if (isUnreportableRequestError(error)) {
        return null;
      }
      event.fingerprint = requestFingerprint(error);
      return event;
    }

    const message = event.exception?.values?.[0]?.value ?? "";
    if (message === "Network Error" || isStaleAssetError(message)) {
      return null;
    }
    // Marketing tags injected via GTM fetch flowershop.media, which fails for every
    // ad-blocking visitor. The fetch runs through Sentry's own wrapper in our bundle, so
    // denyUrls never matches it; Sentry names the host in the message instead.
    if (message.includes("flowershop.media")) {
      return null;
    }
    // Outlook/Defender SafeLinks scanners open emailed links in a headless browser and
    // reject a promise from their own injected script; no real user is involved.
    if (message.includes("Object Not Found Matching Id:")) {
      return null;
    }
    return event;
  },
  // Drop errors originating from browser extensions, and from the Chatlio widget's own
  // handlers (it JSON-parses its server's plaintext token-expiry reply). Sentry matches
  // denyUrls against the innermost non-native frame, so an error one of our listeners
  // throws when the widget dispatches to it still reports.
  denyUrls: [/extension:\/\//i, /^chrome:\/\//i, /chatlio-widget/],
  dsn: "https://e1c828e0bc4e328820127351d0726050@o4508812639797248.ingest.us.sentry.io/4508812641959936",
  integrations: [Sentry.browserTracingIntegration({ router })],
  // Tracing
  tracesSampleRate: isProduction() ? 0.05 : 0.05, //  Capture 5% of the transactions
  environment,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    // Apex Trading domains
    /^https:\/\/app\.apextrading\.com/,
    /^https:\/\/beta\.apextrading\.com/,
    /^https:\/\/demo\.apextrading\.com/,
    /^https:\/\/.*\.apextrading\.com\/api/,
    // Bushel44 domains
    /^https:\/\/beta\.bushel44\.com/,
    /^https:\/\/demo\.bushel44\.com/,
    /^https:\/\/secure\.bushel44\.com/,
    /^https:\/\/.*\.bushel44\.com\/api/,
  ],
  // Session Replay
  replaysSessionSampleRate: isProduction() ? 0.01 : 0.1, // This sets the sample rate at 1% in prod, 10% while in development
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Redundant: Sentry's globalHandlersIntegration already captures all JS errors
// via window.onerror with full stack traces. This handler re-captures the same
// errors as captureMessage (string only, no stack trace), creating duplicates.
// window.addEventListener(
//   "error",
//   (event) => {
//     if (event.message && typeof event.message === "string") {
//       Sentry.captureMessage(`Error in event listener: ${event.message}`);
//     }
//
//     if (event.message && typeof event.message === "string" && event.message.includes("Unable to preload CSS")) {
//       Sentry.captureMessage("CSS preload failed", {
//         extra: { originalMessage: event.message },
//         level: "warning",
//       });
//     }
//
//     if (event.message && typeof event.message === "string" && event.message.includes("Failed to fetch dynamically imported module")) {
//       Sentry.captureMessage("Failed to fetch dynamically imported module captured in error event listener");
//     }
//   },
//   true,
// );

// use store and router
app.use(store);
app.use(router);

// use plugins
plugins(app);

// mount app
app.mount("#app");

// Hold the boot loader until the initial route is rendered AND the CSS has applied, then dismiss —
// so it hands off straight to the loaded, styled app with no white flash before or after. The CSS
// bundle loads non-render-blocking (RouteController stamps the stylesheet media="print"), so a
// resolved theme variable is the "styled" signal; router.isReady() is the "route rendered" signal.
//
// Robustness — this code only runs once app.mount() has succeeded, so the app IS mounted by here:
//   • isReady() can REJECT (a non-asset navigation error), so we set routeReady on both settle
//     paths — the gate never hangs waiting on a route that failed (stale-asset failures reload via
//     router.onError anyway).
//   • MAX_HOLD_MS is a safety valve: never keep a mounted app hidden behind the loader past this,
//     even if a readiness signal misfires. Better a slightly-early reveal than a stuck screen.
//   • A boot that never mounts can't reach here; the 15s fallback in app-loading.blade.php surfaces
//     a refresh prompt for that case.
//   • MIN_VISIBLE_MS (from when the loader first painted, __BOOT_T0__) keeps the mascot from
//     flashing past on a warm boot — tune/remove once the motion is chosen.
// (The stale-asset retry guard in router.js expires by release and time, and is never cleared — clearing it on
// mount would re-arm it every reload and loop.)
{
  const MIN_VISIBLE_MS = 900;
  const MAX_HOLD_MS = 10000;
  const styled = () => getComputedStyle(document.body).getPropertyValue("--sidebar-bg-color").trim() !== "";
  const bootT0 = window.__BOOT_T0__ ?? Date.now(); // capture once so the safety valve always advances
  let routeReady = false;
  const markReady = () => {
    routeReady = true;
  };
  router.isReady().then(markReady).catch(markReady);
  (function tick() {
    const shownFor = Date.now() - bootT0;
    const appReady = routeReady && styled() && shownFor >= MIN_VISIBLE_MS;
    if (appReady || shownFor >= MAX_HOLD_MS) {
      window.__dismissBootLoader?.();
    } else {
      setTimeout(tick, 50);
    }
  })();
}

// Defer Session Replay (the heavy rrweb recorder) until after the app is interactive — it's
// the largest part of the Sentry bundle and isn't needed during boot. Core error capture and
// browser tracing stay eager. Trade-off: anything before this resolves isn't recorded, so a
// very-early-boot error won't have an associated replay.
import("@sentry/vue").then(({ replayIntegration }) => {
  Sentry.addIntegration(replayIntegration());
});
